import {
  Box,
  Center,
  Heading,
  Image,
  useBreakpointValue,
} from "@chakra-ui/react";
import { preloadedServerSideProps } from "~/lib";
import { PageProps } from "~/types";
import {
  fetchItinerary,
  Itinerary as TourismoItinerary,
} from "~/api/resources/Itineraries";
import {
  MapContextProvider,
  useMapContext,
} from "~/providers/MapContextProvider";
import { PhotoAttribute, ReactMarkdown, Subheading } from "~/components";
import { Padding } from "~/components/Layout/Padding";
import { StoryCardCarousel } from "~/components/Carousel/StoryCardCarousel";
import { RoadTripCardCarousel } from "~/components/Carousel/StoryCardCarousel/RoadTripCardCarousel";
import { ItineraryList, ItineraryMap, LegProvider } from "~/components/Maps";
import { SliderProvider } from "~/components/Slider";
import { mediaUrl } from "~/lib/media";
import { GetServerSideProps, NextPage } from "next";

export type Itinerary = TourismoItinerary

type Props = {
  itinerary: Itinerary;
};

const RoadTripSingle: NextPage<Props> = ({ itinerary }) => {
  const { lead_media, name, description } = itinerary;
  const leadImageUrl = mediaUrl(lead_media);
  const tags = itinerary.tags?.filter(
    (tag) => tag.category === "itinerary-story",
  );


  const crow = useBreakpointValue({
    base: "/CrowLogo.png",
    md: "/tabletCrowLogo.png",
  });

  return (
    <SliderProvider>
      <MapContextProvider>
        <>
          <Box
            zIndex={2}
            position={"relative"}
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            <Image
              alt={lead_media?.caption}
              src={leadImageUrl}
              srcSet={`${leadImageUrl} 480w,${leadImageUrl} 800w`}
              sizes="(max-width: 600px) 480px, 800px"
              height={"100vh"}
              minH={600}
              width="100%"
              borderBottom="2px solid white"
              style={{ objectFit: "cover", objectPosition: "center" }}
              sx={{
                maskImage: `url(/hwy3mask-1.png), linear-gradient(#fff 0 0)`,
                maskRepeat: "no-repeat",
                maskPosition: "bottom",
                maskComposite: "exclude",
                WebkitMaskComposite: "destination-out",
                maskSize: "100%, auto",
              }}
            />
            <Box position="absolute" w="100%" h="50%" top={0}>
              <Center
                flexDirection="column"
                mt={{ base: "300px", md: "500px", lg: "300px" }}
              >
                <Subheading
                  textAlign="center"
                  fontSize={{ base: "3xl", md: "4xl" }}
                  color="white"
                >
                  Road trip
                </Subheading>
                <Heading
                  textAlign="center"
                  fontSize={{ base: "6xl", md: "8xl" }}
                  color="white"
                  fontWeight="bold"
                  m="0px 10px"
                >
                  {name}
                </Heading>
              </Center>
              {lead_media?.caption && (
                <Box
                  position="absolute"
                  bottom={{ base: 180, md: 0 }}
                  alignSelf={"flex-start"}
                >
                  <PhotoAttribute>{lead_media.caption}.</PhotoAttribute>
                </Box>
              )}
            </Box>

            <Box
              marginTop={{ base: "50px" }}
              display="flex"
              flexDirection={{ base: "column-reverse" }}
            >
              <Padding mt={{ base: "-100px" }}>
                <Subheading
                  fontSize="2xl"
                  mb={{ base: "56px" }}
                  w={{ base: "100%", md: "75%", lg: "50%" }}
                >
                  {itinerary.subtitle}
                </Subheading>
                <Box w={{ base: "100%", md: "75%", lg: "70%" }}>
                  <ReactMarkdown>{description}</ReactMarkdown>
                </Box>
              </Padding>
              <Image
                src={crow}
                zIndex={1}
                alt=""
                w={{ base: "50%", md: "33%", lg: "20%" }}
                mt={{
                  base: "-80px",
                  md: "-200px",
                  lg: "-250px",
                }}
                alignSelf="flex-end"
              />
            </Box>
          </Box>
        </>

        <Box
          backgroundImage={"/MapTexture.png"}
          padding={{ base: "16px", md: "24px 32px", lg: "24px 64px" }}
          mt="50px"
        >
          <ItineraryLayout {...itinerary} />
        </Box>
        <StoryCardCarousel
          filterByPage
          tags={tags?.map((tag) => tag.id)}
          recordId={itinerary.id}
        />
        <RoadTripCardCarousel itinerary_id={itinerary.id} />
      </MapContextProvider>
    </SliderProvider>
  );
};

const ItineraryLayout = (itinerary: TourismoItinerary) => {
  const { mapSelected } = useMapContext();
  return (
    <LegProvider>
      <Box
        position="relative"
        w={{ base: "100%" }}
        h={{ base: "844px", md: "1194px", lg: "759px" }}
      >
        {!mapSelected && <ItineraryList legs={itinerary.legs} />}
        <ItineraryMap legs={itinerary.legs} />
      </Box>
    </LegProvider>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async (
  context,
) =>
  preloadedServerSideProps<PageProps<Props>>({
    context,
    callback: async () => {
      const { id } = context.query;
      if (typeof id !== "string") return { notFound: true };
      const itinerary = await fetchItinerary(id);
      if (!itinerary) return { notFound: true };

      return {
        props: {
          seoData: {
            metaTitle: `Hwy3 - ${itinerary.name}`,
            metaDescription: itinerary.short_description ?? undefined,
            metaImage: itinerary.lead_media ?? null,
          },
          itinerary,
        },
      };
    },
  });

export default RoadTripSingle;
